import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  Avatar, IconButton, Autocomplete, TextField, Paper,
  Fade, Stack, Chip, Tooltip, CircularProgress, LinearProgress,
  Alert, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import SecurityIcon from '@mui/icons-material/Security';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import DomainIcon from '@mui/icons-material/Domain';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ACCENT = '#0655A5';
const ACCENT_LIGHT = 'rgba(6,85,165,0.07)';

const premiumInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#ffffff',
    fontSize: '0.82rem',
    transition: 'all 0.2s',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8', fontWeight: 500, fontSize: '0.78rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

const DropPaper = (props) => (
  <Paper
    {...props}
    elevation={10}
    sx={{
      borderRadius: 2, mt: 0.5, border: '1px solid #f1f5f9',
      boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
      '& .MuiAutocomplete-listbox': {
        p: 0.5, maxHeight: '28vh',
        '& .MuiAutocomplete-option': {
          borderRadius: 1, mb: 0.2, fontSize: '0.82rem',
          '&:hover': { bgcolor: '#f1f5f9' },
          '&[aria-selected="true"]': { bgcolor: ACCENT_LIGHT, color: ACCENT, fontWeight: 600 },
        },
      },
    }}
  />
);

// ─── Selector de autorizadores reutilizable por CC ─────────────
function CCAutorizadoresEditor({ configKey, config, onChange, dataUsuarioAutorizadores = [], dataUsuarioTesoreros = [] }) {
  const conf = config[configKey] || { autorizadores: [''], tesorero: null };

  const updateConf = (patch) => onChange(configKey, { ...conf, ...patch });

  const addNivel = () => {
    if (conf.autorizadores.length >= 3) return;
    updateConf({ autorizadores: [...conf.autorizadores, ''] });
  };
  const removeNivel = (i) => {
    const list = conf.autorizadores.filter((_, idx) => idx !== i);
    updateConf({ autorizadores: list.length ? list : [''] });
  };
  const updateNivel = (i, val) => {
    const list = [...conf.autorizadores];
    list[i] = val || '';
    updateConf({ autorizadores: list });
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <SecurityIcon sx={{ fontSize: '0.85rem', color: ACCENT }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>Autorizadores para este Centro de Costo</Typography>
        </Box>
        {conf.autorizadores.length < 3 && (
          <Button size="small" onClick={addNivel}
            startIcon={<PersonAddIcon sx={{ fontSize: '0.7rem !important' }} />}
            sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'none', color: ACCENT, bgcolor: ACCENT_LIGHT, px: 0.75, py: 0.15, borderRadius: 1.5, minHeight: 0, '&:hover': { bgcolor: 'rgba(6,85,165,0.14)' } }}>
            + Nivel
          </Button>
        )}
      </Box>

      <Stack spacing={0.7} sx={{ mb: 1 }}>
        {conf.autorizadores.map((autId, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
            <Box sx={{ minWidth: 18, height: 18, borderRadius: '50%', bgcolor: ACCENT_LIGHT, color: ACCENT, fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {idx + 1}
            </Box>
            <Autocomplete fullWidth size="small"
              options={dataUsuarioAutorizadores}
              getOptionLabel={(o) => o.USER_NAME || ''}
              value={dataUsuarioAutorizadores.find(u => u.USUARIOID === autId) || null}
              onChange={(_, v) => updateNivel(idx, v?.USUARIOID || '')}
              PaperComponent={DropPaper}
              noOptionsText="Sin usuarios disponibles"
              renderInput={(p) => <TextField {...p} label={`Nivel ${idx + 1}`} sx={premiumInputSx} size="small" />}
            />
            {idx > 0 && (
              <IconButton size="small" onClick={() => removeNivel(idx)}
                sx={{ color: '#cbd5e1', '&:hover': { color: '#ef4444' }, p: 0.3 }}>
                <CloseIcon sx={{ fontSize: '0.75rem' }} />
              </IconButton>
            )}
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.6 }}>
        <PaymentIcon sx={{ fontSize: '0.85rem', color: '#0ea5e9' }} />
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>Tesorero</Typography>
      </Box>
      <Autocomplete fullWidth size="small"
        options={dataUsuarioTesoreros}
        getOptionLabel={(o) => o.USER_NAME || ''}
        value={dataUsuarioTesoreros.find(u => u.USUARIOID === conf.tesorero) || null}
        onChange={(_, v) => updateConf({ tesorero: v?.USUARIOID || null })}
        PaperComponent={DropPaper}
        noOptionsText="Sin tesoreros disponibles"
        renderInput={(p) => <TextField {...p} label="Tesorero (opcional)" sx={premiumInputSx} size="small" />}
      />
    </Box>
  );
}

// ─── MODAL PRINCIPAL ENFOCADO POR COMPRADOR ──────────────────────────────────
const ModalSincronizarERP = ({
  isOpen,
  handleClose,
  onPreview,
  onConfirmar,
  loadingPreview,
  loadingSync,
  previewData,          // { compradores, totalNuevos, usuarios, usuarioEmpresas }
  dataUsuarioAutorizadores = [],
  dataUsuarioTesoreros = [],
}) => {
  // config: { [`${compradorId}_${empresaId}_${ccId}`]: { autorizadores: [], tesorero: null } }
  const [config, setConfig] = useState({});
  const [selectedCompradorIdx, setSelectedCompradorIdx] = useState(0);

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setConfig({});
      setSelectedCompradorIdx(0);
    }
  }, [isOpen]);

  // Auto preview al abrir
  useEffect(() => {
    if (isOpen && !previewData && !loadingPreview) {
      onPreview();
    }
  }, [isOpen]); // eslint-disable-line

  const updateCCConfig = (key, newConf) => setConfig(prev => ({ ...prev, [key]: newConf }));

  const compradores = useMemo(() => previewData?.compradores || [], [previewData]);
  const currentComprador = compradores[selectedCompradorIdx] || null;

  // Lista general de todos los usuarios
  const allUsuarios = useMemo(() => {
    if (previewData?.usuarios && previewData.usuarios.length > 0) {
      return previewData.usuarios;
    }
    // Fallback a los props
    const combined = [...dataUsuarioAutorizadores, ...dataUsuarioTesoreros];
    const map = new Map();
    combined.forEach(u => { if (u?.USUARIOID) map.set(u.USUARIOID, u); });
    return Array.from(map.values());
  }, [previewData, dataUsuarioAutorizadores, dataUsuarioTesoreros]);

  // Mapa de usuario -> Set de empresas
  const usuarioEmpresasMap = useMemo(() => {
    const map = new Map();
    (previewData?.usuarioEmpresas || []).forEach(ue => {
      if (!map.has(ue.USUARIOID)) map.set(ue.USUARIOID, new Set());
      map.get(ue.USUARIOID).add(ue.EMPRESAID);
    });
    return map;
  }, [previewData]);

  // Helper: obtener usuarios autorizadores para una empresa específica
  const getUsuariosEmpresa = (empresaId) => {
    if (!allUsuarios.length) return [];
    if (!usuarioEmpresasMap.size) return allUsuarios; // Fallback: mostrar todos los usuarios si no hay filtro de empresas

    const filtrados = allUsuarios.filter(u => {
      const empresasUser = usuarioEmpresasMap.get(u.USUARIOID);
      if (!empresasUser) return false;
      return empresasUser.has(empresaId) || empresasUser.has(4); // 4 = ESEASA Global
    });

    return filtrados.length > 0 ? filtrados : allUsuarios; // Si no hay específicos, fallback a todos los activos
  };

  // Helper: obtener tesoreros (TIPOUSERID == 3) para una empresa específica
  const getTesorerosEmpresa = (empresaId) => {
    const usuariosEmp = getUsuariosEmpresa(empresaId);
    const tesoreros = usuariosEmp.filter(u => Number(u.TIPOUSERID) === 3);
    // Si no hay tesoreros de TIPOUSERID 3 específicos, retornar todos los usuarios de la empresa como fallback
    return tesoreros.length > 0 ? tesoreros : usuariosEmp;
  };

  // Aplicar autorizadores a todos los CC de UNA empresa específica de UN comprador
  const applyToEmpresaOfComprador = (compradorId, empresaId, confEjemplo) => {
    const comp = compradores.find(c => c.compradorId === compradorId);
    if (!comp) return;
    const emp = comp.empresas.find(e => e.empresaId === empresaId);
    if (!emp) return;

    const patch = {};
    emp.centrosCosto.forEach(cc => {
      patch[`${compradorId}_${empresaId}_${cc.ccId}`] = { ...confEjemplo };
    });
    setConfig(prev => ({ ...prev, ...patch }));
  };

  // Conteo real de CC configurados válidos
  const totalConfiguradosReal = useMemo(() => {
    return Object.entries(config).filter(([key, c]) =>
      !key.startsWith('bulk_') && c.autorizadores?.some(a => a)
    ).length;
  }, [config]);

  // Conteo de CC configurados para el comprador seleccionado actualmente
  const totalConfiguradosActual = useMemo(() => {
    if (!currentComprador) return 0;
    const prefix = `${currentComprador.compradorId}_`;
    return Object.entries(config).filter(([key, c]) =>
      key.startsWith(prefix) && c.autorizadores?.some(a => a)
    ).length;
  }, [config, currentComprador]);


  const isValid = totalConfiguradosReal > 0;

  const handleConfirmar = (soloCompradorActual = false) => {
    const configArray = [];
    const compradorIdTarget = soloCompradorActual ? currentComprador?.compradorId : null;

    Object.entries(config).forEach(([key, conf]) => {
      if (key.startsWith('bulk_')) return;
      const parts = key.split('_');
      if (parts.length !== 3) return;
      
      const [compVal, empVal, ccVal] = parts.map(Number);
      if (!compVal || !empVal || !ccVal) return;

      // Si es solo comprador actual, filtrar
      if (compradorIdTarget && compVal !== compradorIdTarget) return;

      const autorizadoresLimpios = (conf.autorizadores || []).filter(Boolean);
      if (!autorizadoresLimpios.length) return;

      configArray.push({
        compradorId: compVal,
        empresaId: empVal,
        ccId: ccVal,
        autorizadores: autorizadoresLimpios,
        tesorero: conf.tesorero || null,
      });
    });
    onConfirmar(configArray);
  };

  // ─── PASO 1: Cargando ──────────────────────────────────────────────────────
  const renderLoading = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2.5 }}>
      <Box sx={{ position: 'relative', width: 64, height: 64 }}>
        <CircularProgress size={64} thickness={2} sx={{ color: '#e2e8f0', position: 'absolute' }} variant="determinate" value={100} />
        <CircularProgress size={64} thickness={2} sx={{ color: ACCENT, position: 'absolute' }} />
        <Avatar sx={{ width: 64, height: 64, bgcolor: ACCENT_LIGHT, color: ACCENT, position: 'absolute' }}>
          <SearchIcon sx={{ fontSize: '1.5rem' }} />
        </Avatar>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>Analizando Compradores ERP...</Typography>
        <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>Buscando compradores con registros sin autorizador asignado</Typography>
      </Box>
      <LinearProgress sx={{ width: '60%', borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }} />
    </Box>
  );

  // ─── PASO 2: Sin resultados ────────────────────────────────────────────────
  const renderEmpty = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
      <Avatar sx={{ width: 64, height: 64, bgcolor: '#f0fdf4', color: '#16a34a' }}>
        <CheckCircleIcon sx={{ fontSize: '2rem' }} />
      </Avatar>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>Todo sincronizado</Typography>
        <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>Todos los compradores de la ERP ya cuentan con sus niveles de autorización.</Typography>
      </Box>
    </Box>
  );

  // ─── PASO 3: Vista Enfocada por Comprador ──────────────────────────────────
  const renderPreview = () => {
    if (!currentComprador) return null;

    return (
      <Stack spacing={2}>
        {/* Resumen Superior */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={<PersonIcon sx={{ fontSize: '0.85rem !important' }} />}
            label={`${compradores.length} Comprador${compradores.length > 1 ? 'es' : ''} pendiente${compradores.length > 1 ? 's' : ''}`}
            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '0.75rem' }}
          />
          <Chip
            icon={<VisibilityIcon sx={{ fontSize: '0.85rem !important' }} />}
            label={`${previewData.totalNuevos} registros detectados`}
            sx={{ bgcolor: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.75rem' }}
          />
          {totalConfiguradosReal > 0 && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '0.85rem !important', color: '#16a34a !important' }} />}
              label={`${totalConfiguradosReal} CC listos para guardar`}
              sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 700, fontSize: '0.75rem' }}
            />
          )}
        </Box>

        {/* Pestañas de Compradores */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc', px: 1, pt: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', px: 1.5, pb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Selecciona el Comprador a configurar:
            </Typography>
            <Tabs
              value={selectedCompradorIdx}
              onChange={(_, v) => setSelectedCompradorIdx(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 42,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  minHeight: 42,
                  borderRadius: '8px 8px 0 0',
                  px: 2,
                },
                '& .Mui-selected': { color: ACCENT, bgcolor: '#ffffff' }
              }}
            >
              {compradores.map((comp, idx) => (
                <Tab
                  key={comp.compradorId}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 22, height: 22, bgcolor: selectedCompradorIdx === idx ? ACCENT : '#cbd5e1', fontSize: '0.65rem' }}>
                        {comp.compradorNombre?.charAt(0) || 'U'}
                      </Avatar>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.1 }}>
                          {comp.compradorNombre}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 500 }}>
                          ERP: {comp.compradorErp}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Contenido del Comprador Seleccionado */}
          <Box sx={{ p: 2, bgcolor: '#ffffff' }}>
            <Alert severity="info" icon={<WarningAmberIcon fontSize="small" />} sx={{ mb: 2, fontSize: '0.75rem', py: 0.5 }}>
              Configurando a <strong>{currentComprador.compradorNombre}</strong> ({currentComprador.compradorErp}).
              Haz clic en cada Empresa para desplegar sus Centros de Costo.
            </Alert>

            {/* Lista de Empresas del Comprador (Acordeones Colapsables) */}
            <Stack spacing={1.5}>
              {currentComprador.empresas.map((emp) => {
                const bulkKey = `bulk_emp_${currentComprador.compradorId}_${emp.empresaId}`;

                // Obtener autorizadores y tesoreros específicos para la empresa actual
                const usuariosEmpresaList = getUsuariosEmpresa(emp.empresaId);
                const tesorerosEmpresaList = getTesorerosEmpresa(emp.empresaId);

                // Contar cuántos CC de esta empresa específica ya están configurados
                const ccsConfiguradosCount = emp.centrosCosto.filter(cc => {
                  const ccKey = `${currentComprador.compradorId}_${emp.empresaId}_${cc.ccId}`;
                  return config[ccKey]?.autorizadores?.some(a => a);
                }).length;

                return (
                  <Accordion key={emp.empresaId} defaultExpanded={false}
                    elevation={0}
                    sx={{
                      border: ccsConfiguradosCount > 0 ? '1.5px solid #86efac' : '1px solid #cbd5e1',
                      borderRadius: '12px !important', overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}>
                    
                    {/* Header de Empresa */}
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ACCENT }} />}
                      sx={{ px: 2, py: 0.5, bgcolor: ccsConfiguradosCount > 0 ? '#f0fdf4' : '#f8fafc' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                        <DomainIcon sx={{ fontSize: '1.1rem', color: ACCENT }} />
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b' }}>
                          {emp.empresaNombre}
                        </Typography>

                        {ccsConfiguradosCount > 0 ? (
                          <Chip size="small"
                            icon={<CheckCircleIcon sx={{ fontSize: '0.7rem !important', color: '#15803d !important' }} />}
                            label={`${ccsConfiguradosCount}/${emp.centrosCosto.length} CC listos`}
                            sx={{ height: 20, fontSize: '0.65rem', ml: 'auto', fontWeight: 700, bgcolor: '#dcfce7', color: '#15803d' }}
                          />
                        ) : (
                          <Chip size="small"
                            label={`${emp.centrosCosto.length} Centros de Costo`}
                            sx={{ height: 20, fontSize: '0.65rem', ml: 'auto', fontWeight: 700, bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 2, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                      {/* Quick Bulk para esta Empresa Específica */}
                      <Box sx={{ p: 1.5, mb: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                        <Typography sx={{ fontSize: '0.73rem', color: '#334155', mb: 0.25, fontWeight: 700 }}>
                          ⚡ Aplicar mismos autorizadores a TODOS los CC de {emp.empresaNombre}:
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <CCAutorizadoresEditor
                              configKey={bulkKey}
                              config={config}
                              onChange={updateCCConfig}
                              dataUsuarioAutorizadores={usuariosEmpresaList}
                              dataUsuarioTesoreros={tesorerosEmpresaList}
                            />
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              const bulkConf = config[bulkKey];
                              if (bulkConf && bulkConf.autorizadores?.some(a => a)) {
                                applyToEmpresaOfComprador(currentComprador.compradorId, emp.empresaId, bulkConf);
                              }
                            }}
                            startIcon={<ArrowForwardIcon sx={{ fontSize: '0.8rem !important' }} />}
                            sx={{
                              mt: 0.5, px: 1.5, py: 0.7, fontSize: '0.72rem', fontWeight: 800,
                              textTransform: 'none', bgcolor: ACCENT, borderRadius: 1.5,
                              boxShadow: 'none', whiteSpace: 'nowrap',
                              '&:hover': { bgcolor: '#05478a' },
                            }}
                          >
                            Aplicar a esta Empresa
                          </Button>
                        </Box>
                      </Box>

                      {/* Centros de Costo de esta Empresa (Ordenados A-Z) */}
                      <Stack spacing={1.25}>
                        {[...emp.centrosCosto].sort((a, b) => {
                          const labelA = `${a.numCC} ${a.nombreCC}`.trim();
                          const labelB = `${b.numCC} ${b.nombreCC}`.trim();
                          return labelA.localeCompare(labelB, undefined, { numeric: true, sensitivity: 'base' });
                        }).map((cc) => {
                          const ccKey = `${currentComprador.compradorId}_${emp.empresaId}_${cc.ccId}`;
                          const ccConf = config[ccKey];
                          const ccConfigurado = ccConf?.autorizadores?.some(a => a);

                          return (
                            <Box key={cc.ccId}
                              sx={{
                                border: ccConfigurado ? '1.5px solid #86efac' : '1px solid #e2e8f0',
                                borderRadius: 2, p: 1.5,
                                bgcolor: ccConfigurado ? '#f0fdf4' : '#fafafa',
                              }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Avatar sx={{ width: 24, height: 24, bgcolor: ccConfigurado ? '#dcfce7' : '#f1f5f9', color: ccConfigurado ? '#16a34a' : '#64748b' }}>
                                  {ccConfigurado ? <CheckCircleIcon sx={{ fontSize: '0.8rem' }} /> : <AccountBalanceIcon sx={{ fontSize: '0.8rem' }} />}
                                </Avatar>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
                                  {cc.numCC && `[${cc.numCC}] `}{cc.nombreCC || `CC ${cc.ccId}`}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexWrap: 'wrap' }}>
                                  {cc.departamentos.map((dept, di) => (
                                    <Chip key={di} size="small"
                                      label={`Depto: ${dept.departamentoNombre}`}
                                      sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}
                                    />
                                  ))}
                                </Box>
                              </Box>

                              <CCAutorizadoresEditor
                                configKey={ccKey}
                                config={config}
                                onChange={updateCCConfig}
                                dataUsuarioAutorizadores={usuariosEmpresaList}
                                dataUsuarioTesoreros={tesorerosEmpresaList}
                              />
                            </Box>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      </Stack>
    );
  };

  const showEmpty = !loadingPreview && previewData && previewData.totalNuevos === 0;
  const showPreview = !loadingPreview && previewData && previewData.totalNuevos > 0;

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md" disableRestoreFocus TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 4, overflow: 'hidden',
          boxShadow: '0 25px 60px -12px rgba(0,0,0,0.3)',
          maxHeight: '92vh',
        },
      }}>

      {/* Header */}
      <Box sx={{
        px: 3, py: 1.75,
        background: `linear-gradient(135deg, ${ACCENT} 0%, #1e40af 100%)`,
        color: 'white', display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.18)', color: 'white' }}>
          <SyncIcon sx={{ fontSize: '1.1rem' }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.97rem', lineHeight: 1 }}>
            Sincronización Enfocada por Comprador
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.7rem', mt: 0.3 }}>
            Configuración segura por empresa y centro de costo
          </Typography>
        </Box>

        <IconButton onClick={handleClose} size="small" sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}>
          <CloseIcon sx={{ fontSize: '1.1rem' }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc', overflowY: 'auto' }}>
        {loadingPreview && renderLoading()}
        {showEmpty && renderEmpty()}
        {showPreview && renderPreview()}
      </DialogContent>

      {(showEmpty || showPreview) && (
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9', gap: 1.5 }}>
          <Button onClick={handleClose} sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.8rem' }}>
            Cancelar
          </Button>

          {showPreview && (
            <>
              {/* Botón Guardar SOLO el comprador actual */}
              {totalConfiguradosActual > 0 && (
                <Button
                  variant="outlined"
                  disabled={loadingSync}
                  onClick={() => handleConfirmar(true)}
                  startIcon={loadingSync ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '0.9rem !important' }} />}
                  sx={{
                    px: 2, py: 0.8, fontWeight: 700, borderRadius: 2,
                    textTransform: 'none', fontSize: '0.8rem', color: ACCENT, borderColor: ACCENT,
                    '&:hover': { bgcolor: ACCENT_LIGHT },
                  }}>
                  {`Guardar solo a ${currentComprador?.compradorNombre?.split(' ')[0] || ''} (${totalConfiguradosActual} CC)`}
                </Button>
              )}

              {/* Botón Guardar TODO lo configurado */}
              <Tooltip title={!isValid ? 'Configura al menos un Autorizador en algún Centro de Costo' : ''} arrow>
                <span>
                  <Button
                    variant="contained"
                    disabled={!isValid || loadingSync}
                    onClick={() => handleConfirmar(false)}
                    startIcon={loadingSync
                      ? <CircularProgress size={14} color="inherit" />
                      : <SaveIcon sx={{ fontSize: '1rem !important' }} />
                    }
                    sx={{
                      px: 3, py: 0.9, fontWeight: 800, borderRadius: 2, bgcolor: ACCENT,
                      textTransform: 'none', fontSize: '0.85rem',
                      boxShadow: '0 4px 14px rgba(6,85,165,0.25)',
                      '&:hover': { bgcolor: '#05478a', transform: 'translateY(-1px)' },
                      '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' },
                    }}>
                    {loadingSync
                      ? 'Guardando...'
                      : `Guardar Todo (${totalConfiguradosReal} CC)`
                    }
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ModalSincronizarERP;
